import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { createQuery } from '@shared/lib/query-helpers';

export const assigneeOptionsPageSize = 10;
export const assigneeSearchMaxLength = 200;
type AssigneeOptionsQuery = NonNullable<
  operations['getApiV1TasksByTaskUuidAssigneeOptions']['parameters']['query']
>;
export type AssigneeRoleGroup = NonNullable<AssigneeOptionsQuery['roleGroup']>;
export const assigneeRoleGroups = [
  'helpdesk',
  'admin',
  'super-admin',
  'users',
] as const satisfies readonly AssigneeRoleGroup[];
const maxPage = 100_000;
type AssigneeOptions =
  operations['getApiV1TasksByTaskUuidAssigneeOptions']['responses'][200]['content']['application/json'];
const querySchema = z
  .object({
    q: z.string().trim().max(assigneeSearchMaxLength).optional(),
    roleGroup: z.enum(assigneeRoleGroups).optional(),
    page: z.number().int().min(1).max(maxPage),
    pageSize: z.literal(assigneeOptionsPageSize),
  })
  .strict();

export const assigneeOptionsSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        id: z.uuid(),
        displayName: z.string().nullable(),
        avatarUrl: z.url().nullable().optional(),
      })
    ),
  }),
  meta: z.object({
    page: z.number().int().positive(),
    pageSize: z.literal(assigneeOptionsPageSize),
    totalItems: z.number().int().nonnegative().nullable(),
    totalPages: z.number().int().nonnegative().nullable(),
    countState: z.enum(['exact', 'pending', 'unavailable']),
    countAsOf: z.iso.datetime().nullable(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
}) satisfies z.ZodType<AssigneeOptions>;

/** Task-scoped candidates never use the broader CRM directory response or cache. */
export function helpdeskAssigneeOptionsQuery(
  viewerAccessScope: string,
  taskUuid: string,
  query: { q?: string; page: number; roleGroup?: AssigneeRoleGroup }
) {
  const id = z.uuid().parse(taskUuid);
  const params = querySchema.parse({ ...query, pageSize: assigneeOptionsPageSize });
  return createQuery({
    path: `/api/v1/tasks/${encodeURIComponent(id)}/assignee-options`,
    queryKey: ['helpdesk', viewerAccessScope, 'assignee-options', id, params],
    schema: assigneeOptionsSchema,
    requestOptions: { params },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}
